import { Box } from '@material-ui/core'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import ExpandMoreIcon from '@material-ui/icons/ExpandMoreOutlined'
import * as Sentry from '@sentry/react'
import { push as pushAction } from 'connected-react-router'
import { authSelectors } from 'ducks/auth'
import { orgSelectors } from 'ducks/orgs'
import Alert from 'elements/Alert'
import CustomField from 'elements/field/CustomField'
import { useBulkActionButtons } from 'elements/hooks/useBulkActionButtons'
import { StandardInputs } from 'elements/input/StandardInputs'
import { useEditShareable } from 'elements/react-admin/EditShareable'
import { List } from 'elements/react-admin/List'
import ListActions from 'elements/react-admin/ListActions'
import TextFieldWithSharedEntityIcon from 'elements/TextFieldWithSharedEntityIcon'
import _ from 'lodash'
import useCheckForActiveEditor from 'myenergy/mainContent/components/proposalV2/useCheckForActiveEditor'
import { getDefaultGrapesSwipeTemplateSettings } from 'opensolar-common'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  ComponentVersions_3_0,
  ComponentVersionsInherit,
  Skeleton,
  styled,
  Switch,
  Typography,
  makeStyles,
} from 'opensolar-ui'
import { ProposalV2TemplateSettingsContextProvider } from 'projectSections/form/context/ProposalV2TemplateSettingsContext'
import PropTypes from 'prop-types'
import React, { Suspense, useEffect, useState } from 'react'
import {
  BooleanField,
  BooleanInput,
  Create,
  Datagrid,
  FileField,
  FileInput,
  FormDataConsumer,
  ImageField,
  ImageInput,
  RadioButtonGroupInput,
  SelectArrayInput,
  SelectInput,
  showNotification as showNotificationAction,
  SimpleForm,
  TextInput,
  useTranslate,
} from 'react-admin'
import { useForm, useFormState } from 'react-final-form'
import { connect, useSelector } from 'react-redux'
import ShareabilityFilters from 'resources/connectedOrgs/ShareabilityFilters'
import {
  PROPOSAL_V2_BESPOKE_ORG_SETTINGS_FIELDS,
  PROPOSAL_V2_GRAPES_SWIPE_TEMPLATE_INPUTS_FIELDS,
} from 'resources/proposalTemplates/constants'
import { isProposalV2Type, ProposalTemplateTypeEnum } from 'resources/proposalTemplates/types'
import { useFeatureFlag } from 'util/featureFlags'
import { duplicate as duplicateAction } from '../../actions/restActions'
import EditOrImportButton from '../../elements/button/EditOrImportButton'
import CreateEditToolbar from '../../elements/CreateEditToolbar'
import FileFieldClearable from '../../elements/field/FileFieldClearable'
import ImageFieldClearable from '../../elements/field/ImageFieldClearable'
import CustomCheckboxGroupInput from '../../elements/input/CustomCheckboxGroupInput'
import DependentInput from '../../elements/input/DependentInput'
import EditorComponent from '../../elements/wysiwyg/EditorComponentWrapper'
import { getRoleFromState, renderDraftEditor, renderRawHtmlEditor, toCapitalise } from '../../util/misc'
import { fieldsBespoke } from './BespokeFromContent'
import { CustomFieldTitle } from './classic/InlineDialogPreview'
import { PROPOSAL_TEMPLATE_INPUTS_FIELDS } from './constants'
import FeatureFiguresSelector from './FeatureFiguresSelector'
import CreateButton from './ProposalTemplateCreateButton'
import {
  getDefaultProposalV2Config,
  getProposalV2GrapesSwipeConfig,
  mergeTemplateSettingsIntoProposalV2Config,
} from './proposalV2/getProposalV2Config'
import SectionConfiguration from './SectionConfiguration'

// Lazy-loaded so the classic preview tree (PreviewMyEnergy, BasicLayout, ClassicProposalEditSteps)
// stays out of the entry bundle. ProposalTemplates.jsx is statically imported by several callers
// (e.g. ProposalTemplateEditor, EditProposalTemplateDialog, AILeadGenForm), so a top-level static
// import of this file would drag the preview deps into the eager bundle.
const EditFormContentMaybeWithPreview = React.lazy(() => import('./classic/EditFormContentWithPreview'))

const DEFAULT_PROPOSAL_TEMPLATE_SETTINGS = {
  hide_export_credit_breakdown: false,
  lock_y_axis_across_all_options: true,
  checkout_hidden: false,
}

const useStyles = makeStyles(
  {
    hidePreviews: {
      '& .previews': { display: 'none' },
    },
  },
  { name: 'FileInput' }
)

const StyledAccordion = styled(Accordion)({
  '&.Mui-expanded': {
    margin: '10px 0',
  },
  margin: '10px 0',
})

// export const ProposalTemplateFilter = (props) => (
//   <Filter {...props}>
//     <TextInput label="pos.search" source="q" style={standardStyles.FILTER_FIELD_STYLE} />
//     <QuickFilter source="show_exhibitor_content" label="Show Exhibitor Content" defaultValue={1} />
//     <QuickFilter source="show_only_archived" defaultValue={1} />
//   </Filter>
// )

const ProposalTemplateActions = (props) => {
  const { filterValues, setFilters } = props
  const isStaff = useSelector(authSelectors.getIsStaff)

  const translate = useTranslate()
  const [viewPartnerTemplates, setViewPartnerTemplates] = useState(
    filterValues?.show_exhibitor_content === 1 ? true : false
  )
  const [excludeProjectLevelTemplate, setExcludeProjectLevelTemplate] = useState(
    !!filterValues?.exclude_exclusive_project
  )

  const togglePartnerTemplate = () => {
    setViewPartnerTemplates(!viewPartnerTemplates)
    setFilters({ ...filterValues, show_exhibitor_content: viewPartnerTemplates ? 0 : 1 })
  }

  const toggleProposalV2Templates = () => {
    setExcludeProjectLevelTemplate(!excludeProjectLevelTemplate)
    setFilters({ ...filterValues, exclude_exclusive_project: !excludeProjectLevelTemplate })
  }

  return (
    <>
      {isStaff && (
        <>
          <FormControlLabel
            style={{ margin: '8px' }}
            control={<Switch checked={viewPartnerTemplates} onChange={togglePartnerTemplate} color="primary" />}
            label={translate('View Partner Templates')}
            labelPlacement="start"
          />

          <FormControlLabel
            style={{ margin: '8px' }}
            control={
              <Switch checked={excludeProjectLevelTemplate} onChange={toggleProposalV2Templates} color="primary" />
            }
            label={translate('Exclude Project Level Template')}
            labelPlacement="start"
          />
        </>
      )}
      <ListActions {...props} hasArchived={true} createButtonLabel="Add Proposal Template" hasCreate={false} />
      {props.hasCreate && <CreateButton />}
    </>
  )
}

export const ProposalTemplateList = connect(
  (state) => {
    return {
      org_id: state.auth ? state.auth.org_id : null,
      api_key_pvsell: orgSelectors.getOrg(state)?.api_key_pvsell,
      country_iso2: orgSelectors.getOrgIso2(state),
    }
  },
  { duplicate: duplicateAction }
)(({ accessRights: { allowView, allowCreate, allowEdit, allowDelete }, ...props }) => {
  const isAdmin = useSelector((state) => Boolean(getRoleFromState(state)?.is_admin))
  const bulkActionButtons = useBulkActionButtons(props, allowEdit, allowDelete, allowEdit)

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <List
        hasSearch={true}
        alwayShowActions={true}
        extraCreateButton={<CreateButton />}
        actions={<ProposalTemplateActions api_key_pvsell={props.api_key_pvsell} country_iso2={props.country_iso2} />}
        perPage={20}
        {...props}
        hasCreate={allowCreate}
        bulkActionButtons={bulkActionButtons}
        filters={<ShareabilityFilters />}
        filterDefaultValues={{ exclude_exclusive_project: true }}
      >
        <Datagrid duplicate={props.duplicate} isAdmin={isAdmin}>
          <TextFieldWithSharedEntityIcon source="title" label="Title" />
          <BooleanField source="is_default" textAlign={'center'} displayInCard={true} sortable={false} />
          <EditOrImportButton
            sortable={false}
            source="actions"
            label="Actions"
            org_id={props.org_id}
            resource="proposal_templates"
            duplicate={props.duplicate}
            isAdmin={isAdmin}
            allowEdit={allowEdit}
            allowDelete={allowDelete}
          />
        </Datagrid>
      </List>
    </ComponentVersionsInherit>
  )
})

const inputStyle = {
  width: 256,
}
const defaultChoices = []
const PVSellTemplateSelectInput = () => {
  const [choices, setChoices] = useState(defaultChoices)
  const [isLoading, setIsLoading] = useState(true)
  const translate = useTranslate()
  const api_key_pvsell = useSelector(authSelectors.getPvSellKey)
  const handleEditTemplate = (templateId) => {
    window.open(
      `${window.SUNWIZ_API_DOMAIN}/standalone/proposal-page/update?id=${templateId}&access-token=${api_key_pvsell}`,
      '_blank'
    )
  }
  useEffect(() => {
    const success = (response) => {
      if (response.data) {
        setChoices(response.data)
        setIsLoading(false)
      }
    }
    const error = (error) => {
      console.log(error)
      setIsLoading(false)
    }
    window.$.ajax({
      type: 'GET',
      url: `${window.SUNWIZ_API_DOMAIN}/standalone/project/list-proposals?access-token=${api_key_pvsell}&per-page=1000`,
      dataType: 'json',
      contentType: 'application/json',
      success,
      error,
    })
  }, [])

  return isLoading ? (
    <Skeleton height="50px" />
  ) : (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <SelectInput source="pvprosell_template_id" optionText="name" optionValue="id" choices={choices} />
      <FormDataConsumer>
        {({ formData, ...rest }) =>
          formData.pvprosell_template_id && (
            <Button
              variant="contained"
              style={{ height: 35 }}
              color="primary"
              onClick={() => handleEditTemplate(formData.pvprosell_template_id)}
            >
              <span>{translate('Edit Template')}</span>
            </Button>
          )
        }
      </FormDataConsumer>
    </div>
  )
}

export const ProposalTemplateCreate = (props) => {
  const isAdmin = useSelector((state) => Boolean(getRoleFromState(state)?.is_admin))
  const translate = useTranslate()
  function defaultCreateValue(values) {
    if (props.mode !== undefined) {
      values.type = props.mode
    }

    return values
  }

  const formatSubmitValues = async (values) => {
    if (props.mode === ProposalTemplateTypeEnum.proposalV2_grapes_swipe) {
      const defaultSetting = await getDefaultGrapesSwipeTemplateSettings()
      values.proposal_v2_config = getProposalV2GrapesSwipeConfig(defaultSetting)
    } else if (isProposalV2Type(props.mode)) {
      values.proposal_v2_config = getDefaultProposalV2Config(props.mode)
    }
    return values
  }

  return (
    <Create {...props} component={props.noBorder ? Box : undefined} style={props.noBorder ? { padding: 0 } : {}}>
      <SimpleForm
        redirect="edit"
        defaultValue={defaultCreateValue}
        validate={(values) => validateTitle(values, translate)}
        toolbar={props.toolbar}
        formatSubmitValues={formatSubmitValues}
      >
        <StandardInputs
          archive={isAdmin}
          autoApply={isAdmin && props.mode !== ProposalTemplateTypeEnum.proposalV2_grapes_swipe ? 'is_default' : false}
          autoApplyLabel="Is Default"
          description={false}
          extraFields={props.mode === ProposalTemplateTypeEnum.pvprosell ? [<PVSellTemplateSelectInput />] : []}
        />
      </SimpleForm>
    </Create>
  )
}

export const DraftEditorWithContent = ({ addField = true, ...props }) => {
  const generateId = (fieldName = '') => {
    const parts = fieldName.split('_').map((str) => toCapitalise(str))
    return `wysiwyg${parts.join('')}`
  }

  return (
    <EditorComponent
      label={props.label}
      fieldName={props.fieldName}
      allowUploadImage={props.allowUploadImage}
      charCounterMax={props.charCounterMax}
      mentionContext={props.mentionContext ? props.mentionContext : 'org_and_project_without_project_specific_terms'}
      addField={addField}
      {...props}
      id={generateId(props.fieldName)}
    />
  )
}

const applyDataOrDefaultValue = (configurationValue, path, configurationData, choices) => {
  Object.keys(choices).forEach((key) => {
    _.setWith(
      configurationValue,
      `${path}${key}.value`,
      _.get(configurationData, `${path}${key}.value`, choices[key].defaultValue),
      Object
    )
    choices[key].children &&
      applyDataOrDefaultValue(configurationValue, `${path}${key}.`, configurationData, choices[key].children)
  })
}

const getSettingsData = (settingsData) => {
  if (!settingsData) {
    settingsData = {}
  }

  const newSettingsData = { ...settingsData }
  for (const field in DEFAULT_PROPOSAL_TEMPLATE_SETTINGS) {
    if (settingsData[field] === undefined) {
      newSettingsData[field] = DEFAULT_PROPOSAL_TEMPLATE_SETTINGS[field]
    }
  }
  return newSettingsData
}

const applySettingsDataToForm = function (settingsData, data, fields) {
  if (!data) data = {}
  const formData = getSettingsData(settingsData)
  fields.forEach((field) => {
    switch (field.type) {
      case 'TextInput':
      case 'TextInputDisabled':
      case 'BooleanInput':
      case 'DraftEditorWithContent':
      case 'SelectInput':
        data[field.fieldName] = formData[field.fieldName]

        break

      case 'FeatureFiguresSelector':
      case 'SelectArrayInput':
      case 'SectionConfiguration':
      case 'CustomCheckboxGroupInput':
        if (formData[field.fieldName]) {
          data[field.fieldName] = formData[field.fieldName].split(',')
        } else {
          data[field.fieldName] = []
        }

        break
      case 'ConfigurationCheckboxGroup':
        let configurationValue = {}
        let configurationData = {}
        try {
          if (formData[field.fieldName]) {
            configurationData = JSON.parse(formData[field.fieldName])
          }
        } catch (e) {
          console.warn(e)
        }
        applyDataOrDefaultValue(configurationValue, '', configurationData, field.choices)
        data[field.fieldName] = JSON.stringify(configurationValue)
        break
      case 'RadioButtonGroupInput':
        data[field.fieldName] = formData[field.fieldName]
        break

      default: //ignore
    }
  })

  data.settings = JSON.stringify(data)
  return data
}

const getSettingsFieldListForType = (type) => {
  if (type === 'bespoke') {
    return fieldsBespoke
  }
  if (type === ProposalTemplateTypeEnum.proposalV2_bespoke) {
    return PROPOSAL_V2_BESPOKE_ORG_SETTINGS_FIELDS
  }
  return PROPOSAL_TEMPLATE_INPUTS_FIELDS
}

export const prepareSettingsDataFormSubmit = function (values, fields) {
  let settingsData = {}
  let fieldList = fields ?? getSettingsFieldListForType(values.type)
  let valueAsArray
  let valueAsString
  let valueAsArrayUnsorted

  fieldList.forEach((field) => {
    switch (field.type) {
      case 'TextInput':
      case 'TextInputDisabled':
      case 'BooleanInput':
      case 'DraftEditorWithContent':
      case 'SelectInput':
        settingsData[field.fieldName] = values[field.fieldName]
        break
      case 'SelectArrayInput':
        valueAsArray = values[field.fieldName]
        valueAsString = valueAsArray && valueAsArray.length > 0 ? valueAsArray.join(',') : ''
        settingsData[field.fieldName] = valueAsString
        break
      case 'FeatureFiguresSelector':
        valueAsArray = values[field.fieldName]
        valueAsString = valueAsArray && valueAsArray.length > 0 ? valueAsArray.filter((a) => !!a).join(',') : ''
        settingsData[field.fieldName] = valueAsString
        break
      case 'CustomCheckboxGroupInput':
        valueAsArrayUnsorted = values[field.fieldName] || []
        // let valueAsArray = valueAsArrayUnsorted
        //   ? field.choices.filter(c => valueAsArrayUnsorted.indexOf(c.id) >= 0).map(c => c.id)
        //   : []
        if (Array.isArray(valueAsArrayUnsorted) && valueAsArrayUnsorted.length > 0) {
          valueAsString = valueAsArrayUnsorted.join(',')
          if (valueAsString) settingsData[field.fieldName] = valueAsString
        }

        break
      case 'SectionConfiguration':
        valueAsArrayUnsorted = values[field.fieldName] || []
        // let valueAsArray = valueAsArrayUnsorted
        //   ? field.choices.filter(c => valueAsArrayUnsorted.indexOf(c.id) >= 0).map(c => c.id)
        //   : []
        valueAsArray = valueAsArrayUnsorted
        valueAsString = valueAsArray && valueAsArray.length > 0 ? valueAsArray.join(',') : ''
        if (valueAsString) settingsData[field.fieldName] = valueAsString
        break
      case 'ConfigurationCheckboxGroup':
        settingsData[field.fieldName] = values[field.fieldName]
        break
      case 'RadioButtonGroupInput':
        settingsData[field.fieldName] = values[field.fieldName]
        break

      default: //ignore
    }
  })

  return settingsData
}

export const defaultValueFunc = function (record) {
  let data = {}
  if (record && record.settings && record.settings.length) {
    if (isProposalV2Type(record.type) && record.type !== ProposalTemplateTypeEnum.proposalV2_grapes_swipe) {
      // settings is deprecated with proposal v2 config
    } else {
      try {
        let settingsData = JSON.parse(record.settings)
        const fields =
          record.type === ProposalTemplateTypeEnum.proposalV2_grapes_swipe
            ? PROPOSAL_V2_GRAPES_SWIPE_TEMPLATE_INPUTS_FIELDS
            : PROPOSAL_TEMPLATE_INPUTS_FIELDS
        data = applySettingsDataToForm(settingsData, data, fields)
        data.checkout_hidden = settingsData.checkout_hidden
      } catch (err) {
        Sentry.captureException(err, {
          tags: {
            context: 'ProposalTemplates.defaultValueFunc',
            record_id: record?.id,
            record_type: record?.type,
          },
        })
      }
    }
  }

  if (data.qr_code_toggle === null) data.qr_code_toggle = true
  if (data.qr_code_user_input === null)
    data.qr_code_user_input = '<p>Scan QR code on your phone to access the online proposal.</p>'

  if (!data.proposal_checkout_buttons) {
    // Handle legacy proposal templates that don't have the new fields yet.
    if (data.checkout_hidden) {
      data.proposal_checkout_buttons = 'disabled'
      data.proposal_checkout_message = null
    } else {
      data.proposal_checkout_buttons = 'disable_after_expiry'
      const checkout_message = record.proposal_checkout_message
        ? record.proposal_checkout_message
        : data.proposal_checkout_message
      data.proposal_checkout_message = checkout_message
    }
  }

  data.title = record.title
  data.is_default = record.is_default
  data.proposal_image_file_contents = record.proposal_image_file_contents
  data.co_branding_logo_file_contents = record.co_branding_logo_file_contents
  data.bespoke_is_custom_url = record.bespoke_is_custom_url
  data.bespoke_template_file_contents = record.bespoke_template_file_contents
  data.bespoke_proposal_config = record.bespoke_proposal_config
  data.bespoke_proposal_config_applied = record.bespoke_proposal_config_applied
  data.bespoke_custom_url = record.bespoke_custom_url
  if (record.valid_for_days != null) {
    data.valid_for_days = record.valid_for_days
  }

  data.override_logo_file_contents = record.override_logo_file_contents

  data.assistant = record.assistant || 'disabled'
  data.assistant_custom_prompt = record.assistant_custom_prompt || ''
  if (isProposalV2Type(record.type)) {
    data.proposal_v2_config = record.proposal_v2_config
  }
  return data
}

const mapStateToPropsEdit = (state) => {
  const org = orgSelectors.getOrg(state)
  return {
    org_id: authSelectors.getOrgId(state),
    isAdmin: authSelectors.getIsAdmin(state),
    isStaff: authSelectors.getIsStaff(state),
    enableExhibit: org?.enable_exhibit,
    api_key_pvsell: org?.api_key_pvsell,
  }
}

export const isLockedByOriginalOwner = (record, fieldName) => {
  return record.original && record.locked_sections && record.locked_sections.indexOf(fieldName) !== -1
}

const fieldsInGroups = (fields) => {
  let fieldGroups = []
  let group = null
  fields.forEach((f) => {
    if (f.type === 'h1') {
      if (group) {
        fieldGroups.push(group)
      }
      group = { title: f.title, fields: [] }
    } else {
      group.fields.push(f)
    }
  })
  fieldGroups.push(group)
  return fieldGroups
}

const properCaseUnderscoredString = (underscoredString) =>
  underscoredString.fieldName
    .split('_')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ')

const fieldIsVisible = (field, record, enableExhibit, isStaff) =>
  (!field.visibility || field.visibility(record, enableExhibit) === true) && (isStaff || !field.staff_only)

/**
 * Render proposal-template form inputs grouped by accordion.
 *
 * @param {Object}                    props
 * @param {Object}                    props.record
 * @param {Array}                     props.fields
 * @param {boolean}                   [props.disabled]
 * @param {boolean}                   [props.enableExhibit]
 * @param {string[]}                  [props.disabledGroups]         Accordion titles that should be locked (visually disabled, cannot expand).
 * @param {(title: string) => void}   [props.onDisabledGroupExpand]  Called when the user attempts to expand a locked accordion.
 */
export const RenderInputs = (props) => {
  const { record, fields } = props
  const translate = useTranslate()
  const formState = useFormState()
  const isStaff = useSelector(authSelectors.getIsStaff)
  const form = useForm()
  const disabled = props.disabled === true
  const classes = useStyles()
  const labelOrDefault = (fieldDetails, fallbackToName = true) =>
    fieldDetails.fieldLabel
      ? translate(fieldDetails.fieldLabel)
      : fallbackToName
        ? translate(properCaseUnderscoredString(fieldDetails))
        : undefined

  return !record.settings ? (
    <></>
  ) : (
    <div>
      {fieldsInGroups(fields)
        // Skip any field groups which have no visible fields inside
        .filter(
          (fieldGroup) =>
            fieldGroup.fields.filter((field) => fieldIsVisible(field, props.record, props.enableExhibit, isStaff))
              .length > 0
        )
        .map((fieldGroup, fieldGroupIndex) => (
          <StyledAccordion
            key={fieldGroupIndex + '_' + props.disabled}
            className="Accordion"
            classes={{ expanded: 'ExpansionPanelExpanded' }}
          >
            <AccordionSummary className="AccordionSummary" expandIcon={<ExpandMoreIcon />}>
              {translate(fieldGroup.title)}
            </AccordionSummary>
            <AccordionDetails className="AccordionDetails">
              <div
                style={{
                  // minHeight: 320,
                  position: 'relative',
                  width: '100%',
                  padding: 0,
                }}
              >
                {fieldGroup.fields
                  .filter((field) => fieldIsVisible(field, props.record, props.enableExhibit, isStaff))
                  .map((fieldDetails, i) => {
                    switch (fieldDetails.type) {
                      case 'p':
                        return (
                          <Typography
                            style={{ color: 'rgba(0, 0, 0, 0.54)', fontSize: 12, margin: 0 }}
                            key={fieldGroupIndex + i}
                          >
                            {translate(fieldDetails.title)}
                          </Typography>
                        )
                      case 'h2':
                        return (
                          <Typography variant="h6" key={fieldGroupIndex + i}>
                            {translate(fieldDetails.title)}
                          </Typography>
                        )
                      case 'h5':
                        return <h5 key={fieldGroupIndex + i}>{translate(fieldDetails.title)} h5</h5>
                      case 'Alert':
                        return <Alert severity="info">{translate(fieldDetails.title)}</Alert>
                      case 'BooleanInput':
                        return (
                          <BooleanInput
                            key={fieldGroupIndex + i}
                            component={BooleanInput}
                            name={fieldDetails.fieldName}
                            label={fieldDetails.fieldLabel || fieldDetails.fieldName}
                            source={fieldDetails.fieldName}
                            defaultValue={fieldDetails.defaultValue}
                            disabled={disabled}
                            {...props}
                          />
                        )
                      case 'TextInput': {
                        const elementKey = `${fieldGroupIndex}_${fieldDetails.fieldName}_${i}`
                        return (
                          <CustomField
                            key={elementKey}
                            name={fieldDetails.fieldName}
                            source={fieldDetails.fieldName}
                            style={{ ...inputStyle, marginRight: 40 }}
                            component={TextInput}
                            label={labelOrDefault(fieldDetails)}
                            defaultValue={fieldDetails.defaultValue}
                            options={{
                              disabled: isLockedByOriginalOwner(record, fieldDetails.fieldName) || disabled,
                            }}
                            {...props}
                          />
                        )
                      }
                      case 'DraftEditorWithContent':
                        return (
                          <FormDataConsumer>
                            {({ formData }) => {
                              const shouldShow =
                                !fieldDetails.condition ||
                                formData[fieldDetails.condition] === fieldDetails.conditionOperand ||
                                fieldDetails.conditionOperand?.includes(formData[fieldDetails.condition])

                              if (!shouldShow) return false

                              const elementKey = `${fieldGroupIndex}_${fieldDetails.fieldName}_${i}`
                              return (
                                <div key={elementKey}>
                                  <DependentInput dependsOn={fieldDetails.fieldName} resolve={renderDraftEditor}>
                                    <CustomField
                                      label={
                                        <CustomFieldTitle
                                          label={labelOrDefault(fieldDetails)}
                                          fieldName={fieldDetails.fieldName}
                                          endAdornment={fieldDetails.fieldLabelEndAdornment}
                                        />
                                      }
                                      component={DraftEditorWithContent}
                                      fieldName={fieldDetails.fieldName}
                                      allowUploadImage={fieldDetails.allowUploadImage}
                                      name={fieldDetails.fieldName}
                                      source={fieldDetails.fieldName}
                                      charCounterMax={fieldDetails.charCounterMax}
                                      mentionContext={fieldDetails.mentionContext}
                                      record={record}
                                      resource={props.resource}
                                      disabled={isLockedByOriginalOwner(record, fieldDetails.fieldName) || disabled}
                                    />
                                  </DependentInput>

                                  <DependentInput dependsOn={fieldDetails.fieldName} resolve={renderRawHtmlEditor}>
                                    <CustomField
                                      label={labelOrDefault(fieldDetails)}
                                      component={TextInput}
                                      fullWidth={true}
                                      multiline={true}
                                      style={inputStyle}
                                      fieldName={fieldDetails.fieldName}
                                      name={fieldDetails.fieldName}
                                      source={fieldDetails.fieldName}
                                      {...props}
                                    />
                                  </DependentInput>
                                </div>
                              )
                            }}
                          </FormDataConsumer>
                        )
                      case 'SelectInput':
                        return (
                          <span style={{ marginRight: 40 }}>
                            <CustomField
                              key={fieldGroupIndex + i}
                              name={fieldDetails.fieldName}
                              source={fieldDetails.fieldName}
                              component={SelectInput}
                              style={inputStyle}
                              label={fieldDetails.fieldLabel}
                              choices={fieldDetails.choices.map((obj) => {
                                return { name: translate(obj.name), ...obj }
                              })}
                              options={{
                                disabled: isLockedByOriginalOwner(record, fieldDetails.fieldName) || disabled,
                              }}
                              {...props}
                            />
                          </span>
                        )
                      case 'SelectArrayInput':
                        return (
                          //should we refactor this back to old UI
                          <span style={{ marginRight: 40 }}>
                            <CustomField
                              component={SelectArrayInput}
                              style={inputStyle}
                              key={fieldGroupIndex + i}
                              label={fieldDetails.fieldLabel}
                              fieldName={fieldDetails.fieldName}
                              source={fieldDetails.fieldName}
                              options={{
                                listStyle: { overflow: 'auto', display: 'inline-grid', maxHeight: 200 },
                                disabled: isLockedByOriginalOwner(record, fieldDetails.fieldName) || disabled,
                              }}
                              name={fieldDetails.fieldName}
                              choices={fieldDetails.choices.map((obj) => {
                                return { name: translate(obj.name), ...obj }
                              })}
                              {...props}
                            />
                          </span>
                        )

                      case 'FeatureFiguresSelector':
                        return (
                          <FeatureFiguresSelector
                            key={fieldGroupIndex + i}
                            fieldName={fieldDetails.fieldName}
                            source={fieldDetails.fieldName}
                            options={{
                              listStyle: { overflow: 'auto', display: 'inline-grid', maxHeight: 200 },
                              disabled: isLockedByOriginalOwner(record, fieldDetails.fieldName) || disabled,
                              style: inputStyle,
                            }}
                            name={fieldDetails.fieldName}
                            choices={fieldDetails.choices.map((obj) => {
                              return { name: translate(obj.name), ...obj }
                            })}
                            {...props}
                          />
                        )
                      case 'CustomCheckboxGroupInput':
                        return (
                          <CustomField
                            component={CustomCheckboxGroupInput}
                            key={fieldGroupIndex + i}
                            fieldName={fieldDetails.fieldName}
                            source={fieldDetails.fieldName}
                            name={fieldDetails.fieldName}
                            genericGroup={fieldDetails.genericGroup}
                            choices={fieldDetails.choices}
                            // options={
                            //   {
                            //      disabled: fieldDetails.disabled && fieldDetails.disabled(props.record) === true,
                            //   }
                            // }
                            disabledChoices={
                              disabled ? fieldDetails.choices.map((choice) => choice.id) : fieldDetails.disabledChoices
                            }
                            disabled={disabled}
                          />
                        )
                      case 'SectionConfiguration':
                        return formState.values[fieldDetails.fieldName] ? (
                          <CustomField
                            component={SectionConfiguration}
                            key={fieldGroupIndex + i}
                            choices={fieldDetails.choices.map((obj) => {
                              return { name: translate(obj.name), ...obj }
                            })}
                            fieldName={fieldDetails.fieldName}
                            source={fieldDetails.fieldName}
                            name={fieldDetails.fieldName}
                            updateField={form.mutators.updateField}
                            genericGroup={fieldDetails.genericGroup}
                            options={{
                              style: { width: 300 },
                            }}
                            disabled={isLockedByOriginalOwner(record, fieldDetails.fieldName) || disabled}
                            disabledChoices={
                              //disable generic blocks for solaria template
                              fieldDetails.disabledChoices
                            }
                            resource={props.resource}
                          />
                        ) : null

                      case 'ImageField':
                        return (
                          <CustomField
                            component={ImageFieldClearable}
                            key={fieldGroupIndex + i}
                            source={fieldDetails.fieldName}
                            name={fieldDetails.fieldName}
                            title="title"
                            record={record}
                            disabled={isLockedByOriginalOwner(record, fieldDetails.fieldName) || disabled}
                            resource={props.resource}
                          />
                        )
                      case 'ImageInput':
                        return (
                          !disabled &&
                          (isLockedByOriginalOwner(record, fieldDetails.fieldName) !== true ? (
                            <CustomField
                              component={ImageInput}
                              key={fieldGroupIndex + i}
                              name={fieldDetails.fieldName}
                              source={fieldDetails.fieldName}
                              label="Upload Image"
                              accept="image/*"
                              placeholder={<p>{translate('Drop your image here')}</p>}
                              {...props}
                            >
                              <ImageField source="src" title="title" />
                            </CustomField>
                          ) : null)
                        )
                      case 'FileField':
                        return (
                          <CustomField
                            component={FileFieldClearable}
                            key={fieldGroupIndex + i}
                            source={fieldDetails.fieldName}
                            name={fieldDetails.fieldName}
                            title="title"
                            record={record}
                            disabled={isLockedByOriginalOwner(record, fieldDetails.fieldName) || disabled}
                            resource={props.resource}
                          />
                        )
                      case 'FileInput':
                        return (
                          !disabled &&
                          (isLockedByOriginalOwner(record, fieldDetails.fieldName) !== true ? (
                            <div
                              className={
                                typeof formState.values[fieldDetails.fieldName] === 'string' && classes.hidePreviews
                              }
                            >
                              <CustomField
                                component={FileInput}
                                key={fieldGroupIndex + i}
                                name={fieldDetails.fieldName}
                                source={fieldDetails.fieldName}
                                label={translate('Upload File')}
                                placeholder={<p>{translate('Drop your file here')}</p>}
                                {...props}
                              >
                                <FileField source="src" title="title" />
                              </CustomField>
                            </div>
                          ) : null)
                        )
                      case 'RadioButtonGroupInput':
                        return (
                          <CustomField
                            key={fieldGroupIndex + i}
                            style={{ top: 0, display: 'table', color: 'rgb(162, 160, 160)' }}
                            name={fieldDetails.fieldName}
                            label={fieldDetails.fieldLabel}
                            source={fieldDetails.fieldName}
                            component={RadioButtonGroupInput}
                            choices={fieldDetails.choices.map((obj) => {
                              return { name: translate(obj.name), ...obj }
                            })}
                            options={{
                              initialValue: 'full',
                              disabled: disabled,
                            }}
                            disabled={isLockedByOriginalOwner(record, fieldDetails.fieldName) || disabled}
                            resource={props.resource}
                          />
                        )

                      default:
                        return null
                    }
                  })}
              </div>
            </AccordionDetails>
          </StyledAccordion>
        ))}
    </div>
  )
}

export const formatSubmitValues = (values) => {
  const newSettingsData = prepareSettingsDataFormSubmit(values)
  if (!Array.isArray(values['locked_sections']) || !newSettingsData['locked_sections']) {
    values['locked_sections'] = []
  }

  values.settings = JSON.stringify(newSettingsData)

  // remove `original` field which should not be posted to back-end or will causes errors
  if (values.original) {
    delete values.original
  }
  return values
}

const ProposalTemplateEditForm = ({ accessRights, customToolbar, disablePreview, ...props }) => {
  const translate = useTranslate()

  const { unsavedTemplateSettingsRef, handleCheckForActiveEditor } = useCheckForActiveEditor()
  const enableClassicPreview = useFeatureFlag('enable_classic_proposal_edit_preview', 'on')
  // disablePreview wins over the FF — sidebar callers force no-preview to keep the
  // sub-view layout sane regardless of how the preview FF is set in the env.
  const showClassicPreview = enableClassicPreview && !disablePreview

  const Toolbar = customToolbar || CreateEditToolbar

  return useEditShareable({
    accessRights,
    editProps: props,
    render: ({ access }) => (
      <SimpleForm
        hideCancelButton={!!props.hideCancelButton}
        defaultValue={defaultValueFunc}
        formatSubmitValues={async (values) => {
          await handleCheckForActiveEditor()
          return formatSubmitValues({
            ...values,
            ...(unsavedTemplateSettingsRef.current && {
              proposal_v2_config: mergeTemplateSettingsIntoProposalV2Config(
                values.proposal_v2_config,
                unsavedTemplateSettingsRef.current
              ),
            }),
          })
        }}
        keepDirtyOnReinitialize={true}
        toolbar={
          access.allowEdit ? (
            <Toolbar
              org_id={props.org_id}
              resource="proposal_templates"
              duplicate={props.duplicate}
              redirect="/proposal_templates"
            />
          ) : null
        }
        validate={(values) => validateTitle(values, translate)}
        disabled={!access.allowEdit}
        redirect={(_, __, record) => {
          if (record.type === ProposalTemplateTypeEnum['proposalV2_grapes_swipe']) return false
          if (record.type === ProposalTemplateTypeEnum.opensolar && showClassicPreview) return false
          return '/proposal_templates'
        }}
      >
        <FormDataConsumer>
          {({ formData, ...rest }) => {
            return (
              <Suspense fallback={<Skeleton width="100%" height={400} />}>
                <EditFormContentMaybeWithPreview
                  access={access}
                  enableExhibit={props.enableExhibit}
                  isStaff={props.isStaff}
                  apiKeyPvsell={props.api_key_pvsell}
                  formData={formData}
                  disablePreview={disablePreview}
                  {...rest}
                />
              </Suspense>
            )
          }}
        </FormDataConsumer>
      </SimpleForm>
    ),
  }).editPage
}

ProposalTemplateEditForm.propTypes = {
  push: PropTypes.func,
  record: PropTypes.object,
  showNotification: PropTypes.func,
  customToolbar: PropTypes.elementType,
  disablePreview: PropTypes.bool,
}

const ProposalTemplateEditWithContext = (props) => (
  <ProposalV2TemplateSettingsContextProvider>
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <ProposalTemplateEditForm {...props} />
    </ComponentVersionsInherit>
  </ProposalV2TemplateSettingsContextProvider>
)

export const validateTitle = (values, translate) => {
  const errors = {}
  if (!values.title) {
    errors.title = [translate('Title is required.')]
  }
  return errors
}

export const ProposalTemplateEdit = connect(mapStateToPropsEdit, {
  showNotification: showNotificationAction,
  push: pushAction,
  duplicate: duplicateAction,
})(ProposalTemplateEditWithContext)
